"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { STYLE_COUNT } from "@/data/style-count";
import { formatNumber } from "@/lib/format-number";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import type { ToolUiStrings } from "@/lib/tool-content";
import { substituteTextFacts } from "@/lib/tool-facts";

/**
 * A tool widget's own strings, supplied by context.
 *
 * The same arrangement `citation-tool/tool-strings.tsx` uses for the generator, and for
 * the same reason: a widget's labels are read several components down, by event handlers
 * and by computed results, so prop-drilling them would touch every signature and make an
 * omission silent. The difference is that there is no single dictionary section to type
 * against — there are 250 widgets with 250 unrelated vocabularies — so the *defaults*
 * live with each widget as one exported object, and this context carries only the
 * overrides a translated content file supplies.
 *
 * That inversion is what makes the migration safe. English is defined exactly once, in
 * the component that renders it: an `en.json` carries no `ui` key at all, the context is
 * empty, and `useToolUi` hands back the widget's own defaults unchanged. So moving a tool
 * across cannot re-word it, and an unwrapped widget — rendered in a test, or from an
 * un-migrated page — still renders in English.
 */
interface ToolUiValue {
  strings: ToolUiStrings;
  /** The style count, already grouped for this locale by the route. */
  styleCount: string;
  /** The page's locale. Seven casing tools default a control from it. */
  locale: string;
}

/**
 * The count an unwrapped widget states.
 *
 * A widget rendered outside the provider — in a test, or from the one tool that has not
 * migrated — still has to say a true number, so this falls back to the pinned constant
 * rather than to a placeholder or a hole. English grouping, because an unwrapped widget
 * has no locale to ask; the pin is held against the live catalog by `ai-surfaces.test.ts`.
 */
const FALLBACK: ToolUiValue = {
  strings: {},
  styleCount: STYLE_COUNT.toLocaleString("en-US"),
  locale: DEFAULT_LOCALE,
};

const ToolUiContext = createContext<ToolUiValue>(FALLBACK);

export function ToolUiProvider({
  value,
  styleCount,
  locale,
  children,
}: {
  value?: ToolUiStrings;
  /** From the route: `formatCount(locale, getStyleCount())`. */
  styleCount?: string;
  /** From the route: the page's locale segment. */
  locale?: string;
  children: ReactNode;
}) {
  const resolved = useMemo(
    () => ({
      strings: value ?? {},
      styleCount: styleCount ?? FALLBACK.styleCount,
      locale: locale ?? FALLBACK.locale,
    }),
    [value, styleCount, locale]
  );
  return (
    <ToolUiContext.Provider value={resolved}>{children}</ToolUiContext.Provider>
  );
}

/**
 * Merge overrides over a widget's defaults.
 *
 * Only keys the widget declares are copied, so a translation that still carries a key
 * from a redesigned widget is inert rather than a crash, and a key the translation has
 * not reached yet falls back to English rather than rendering a hole. Nested maps merge
 * key by key at every level for the same reason: a locale that has translated six of a converter's
 * thirty-seven unit labels should show six translated labels, not lose thirty-one.
 */
function isMap(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeValue(base: unknown, override: unknown): unknown {
  if (isMap(base) && isMap(override)) {
    const merged: Record<string, unknown> = { ...base };
    for (const key of Object.keys(base)) {
      if (override[key] !== undefined) {
        merged[key] = mergeValue(base[key], override[key]);
      }
    }
    return merged;
  }
  // A shape mismatch — a list where the widget wants a string — is dropped rather
  // than rendered, so a malformed translation degrades to English.
  if (
    typeof base === typeof override &&
    Array.isArray(base) === Array.isArray(override)
  ) {
    return override;
  }
  return base;
}

export function mergeToolStrings<S extends object>(
  defaults: S,
  overrides: ToolUiStrings
): S {
  return mergeValue(defaults, overrides) as S;
}

/**
 * Resolve `{styleCount}` in every string of a merged set, one level deep.
 *
 * The count is a live fact about the site and six widgets state it inside a sentence
 * they own. Doing it here, once, is what keeps it out of the widgets: a widget writes
 * the placeholder and never imports a number, and — the part that matters while sixteen
 * translations are being written — a translator translates *around* the placeholder
 * instead of copying a literal out of the English source, which is how it went stale
 * twice before.
 */
function resolveFacts<S extends object>(strings: S, styleCount: string): S {
  const facts = { styleCount };
  const walk = (node: unknown): unknown => {
    if (typeof node === "string") {
      return substituteTextFacts(node, facts);
    }
    if (Array.isArray(node)) {
      return node.map(walk);
    }
    if (isMap(node)) {
      return Object.fromEntries(
        Object.entries(node).map(([key, value]) => [key, walk(value)])
      );
    }
    return node;
  };
  return walk(strings) as S;
}

/**
 * @param defaults The widget's English strings. Must be a stable module-level constant —
 *   a fresh object per render would defeat the memo below.
 */
export function useToolUi<S extends object>(defaults: S): S {
  const { strings, styleCount } = useContext(ToolUiContext);
  return useMemo(
    () => resolveFacts(mergeToolStrings(defaults, strings), styleCount),
    [defaults, strings, styleCount]
  );
}

/**
 * The page's locale, for the widgets that need it.
 *
 * Two kinds do. The casing tools use it to pick the default state of one checkbox — a
 * Turkish reader on `/tr` is overwhelmingly casing Turkish text, so the tool starts
 * where they are and stays overridable. The calculators use it to write their answers,
 * through `useToolNumber` below. Separate from `useToolUi` because a widget that wants
 * the locale usually does not want the strings hook re-running for it.
 */
export function useToolLocale(): string {
  return useContext(ToolUiContext).locale;
}

/**
 * Format a result the way the reader's language writes numbers.
 *
 * A dozen calculators rendered their answers through `toLocaleString("en-US")`, which
 * put `1,234.5` into German prose. The hook rather than the bare function because the
 * formatting usually happens in a module-level helper that has no locale in scope, and
 * threading one argument through those helpers is what the change actually is — the
 * helpers stay pure and testable, and the component supplies the locale once.
 *
 * Stable across renders, so a widget can pass it to a `useMemo` dependency list without
 * defeating the memo.
 */
export function useToolNumber(): (
  value: number,
  options?: Intl.NumberFormatOptions
) => string {
  const locale = useContext(ToolUiContext).locale;
  return useMemo(
    () => (value: number, options?: Intl.NumberFormatOptions) =>
      formatNumber(locale, value, options),
    [locale]
  );
}
